import { LegacyDigestRedirect } from './util/digestRoutes';
import ProductTranslationRuntime from './i18n/ProductTranslationRuntime';
import React, { useEffect } from 'react';
import { BrowserRouter, Switch, Route, Redirect, useLocation } from 'react-router-dom';

function ScrollToTop() {
    const { pathname, state } = useLocation();
    useEffect(() => {
        if (state?.preserveScroll) {
            return;
        }
        window.scrollTo(0, 0);
    }, [pathname, state]);
    return null;
}

// react-snap always prerenders "/", "/login", and "/register" as logged-out
// (there is no token during prerendering). Reading localStorage during the
// first render on these routes would make the client's initial render
// disagree with that static HTML, which React treats as a hydration error
// (min. errors #418/#423/#425) and discards the prerendered content.
//
// index.js already knows synchronously, before mounting, whether *this*
// visitor has a token - and skips hydrateRoot entirely for them (falls back
// to a fresh createRoot render, which replaces the prerendered DOM instead
// of needing to match it). So a logged-in visitor never actually needs the
// "assume logged-out" false-start: window.__LUCKYNOTE_INITIAL_HAS_TOKEN__
// tells us it's safe to render the authenticated view from the very first
// paint, with no flash of the landing page. Anonymous visitors (and
// crawlers, who never carry a token) still start at false, matching what
// was actually prerendered, keeping hydration safe for them.
//
// The effect re-runs on every pathname change (not just on mount) so that
// e.g. Login's `history.push('/home')` after setting the token is picked up
// immediately instead of only reflecting the token that existed on boot.
function useHasTokenAfterMount() {
    const { pathname } = useLocation();
    const [hasToken, setHasToken] = React.useState(
        () => Boolean(window.__LUCKYNOTE_INITIAL_HAS_TOKEN__)
    );
    React.useEffect(() => {
        setHasToken(Boolean(localStorage.getItem('token')));
    }, [pathname]);
    return hasToken;
}

import Login from './screens/AuthScreens/Login/Login';
import Register from './screens/AuthScreens/Register/Register';
import Mobile from './screens/Mobile';

// Heavy authenticated screens are code-split so the public/marketing routes
// (which react-snap prerenders) ship a much smaller initial bundle. They are
// prefetched during idle time below, so navigating into the app stays instant.
const SharedMessagePage = React.lazy(() => import('./pages/SharedMessagePage'));
const Home = React.lazy(() => import('./screens/Home'));
const Admin = React.lazy(() => import('./screens/Admin'));
const MessageDeepLink = React.lazy(() => import('./screens/MessageDeepLink'));
import PublishedLinkRouter from './screens/Published/PublishedLinkRouter';
import Verified from './screens/Verified';
// import Welcome from './screens/Welcome';
import LandingHome from './screens/Landing/views/LandingHome';
import MarketingPage from './screens/Landing/views/MarketingPage';
import CliDownloadPage from './screens/Landing/views/CliDownloadPage';
import ComparePage from './screens/Landing/views/ComparePage';
import ForPage from './screens/Landing/views/ForPage';
import UseCasesPage from './screens/Landing/views/UseCasesPage';
import MessageYourselfPage from './screens/Landing/views/MessageYourselfPage';
import GuidesPage from './screens/Landing/views/GuidesPage';
import Privacy from './screens/Landing/views/Privacy';
import AppContainer from './AppContainer';
import Terms from './screens/Landing/views/Terms';
import Contact from './screens/Landing/views/Contact';
import DataSecurity from './screens/Landing/views/DataSecurity';
import Press from './screens/Landing/views/Press';
import About from './screens/Landing/views/About';
import AuthorPage from './screens/Landing/views/AuthorPage';
import ErrorPage from './screens/ErrorScreen/ErrorPage';
import XOAuthErrorPage from './screens/ErrorScreen/XOAuthErrorPage';
import ResetPasswordPage from './screens/AuthScreens/ResetPassword/ResetPasswordPage';
import VerifyAccountPage from './screens/AuthScreens/Verify/VerifyAccountPage';
import OAuthConsentPage from './screens/AuthScreens/OAuthConsent/OAuthConsentPage';
import RaycastAuthPage from './screens/AuthScreens/ExtensionAuth/RaycastAuthPage';
import ExtensionAuthPage from './screens/AuthScreens/ExtensionAuth/ExtensionAuthPage';
import ForgotPasswordPage from './screens/AuthScreens/ForgotPassword/ForgotPasswordPage';
import { StripeProvider } from './components/Payment/PaymentForm';
import PaymentSuccess from './screens/PaymentSuccess';
import PaymentCancel from './screens/PaymentCancel';
import AccountDeleted from './screens/AccountDeleted';
const Checkout = React.lazy(() => import('./screens/Checkout/Checkout'));
const Purchases = React.lazy(() => import('./screens/Checkout/Purchases'));
import BlogList from './screens/Blog/BlogList';
import BlogPost from './screens/Blog/BlogPost';
import ChangelogPage from './screens/Changelog/ChangelogPage';
import LaunchPage from './screens/Launch/LaunchPage';
import ExtensionOnboarding from './screens/ExtensionOnboarding/ExtensionOnboarding';
const MediaManagerPage = React.lazy(() => import('./pages/MediaManagerPage'));
const IntegrationsPage = React.lazy(() => import('./pages/IntegrationsPage'));
const ClaudeMcpPage = React.lazy(() => import('./pages/ClaudeMcpPage'));
const ChatGptMcpPage = React.lazy(() => import('./pages/ChatGptMcpPage'));
const LocalImpersonationPage = React.lazy(() => import('./pages/LocalImpersonationPage'));
const ReviewsPage = React.lazy(() => import('./pages/ReviewsPage'));
const ReviewDetailPage = React.lazy(() => import('./pages/ReviewDetailPage'));
const WrappedPage = React.lazy(() => import('./pages/WrappedPage'));
const SharedFolderPage = React.lazy(() => import('./pages/SharedFolderPage'));
const TrialFeedbackPage = React.lazy(() => import('./pages/TrialFeedbackPage'));
import NotFoundPage from './pages/NotFoundPage';
import RouteSeo from './seo/RouteSeo';
import { getPostLogoutRedirect } from './util/logoutRedirect';
import * as COLOR from './appearance/Colors';
import { MARKETING_PAGE_META } from './data/marketingPageMeta';

// Home/Admin/etc. render their own dark PRIMARY_COLOR background as soon as
// they mount. Before code-splitting, that happened synchronously with no
// gap; now their chunk has to be fetched first, and a `null` Suspense
// fallback showed the page's default white background during that gap -
// a visible flash for anyone reloading while logged in. Matching the
// fallback's background to what the lazy screen renders removes the flash.
function AppChunkFallback() {
    return <div style={{ minHeight: '100vh', backgroundColor: COLOR.PRIMARY_COLOR }} />;
}

// Lightweight route/metadata manifest (path/title/icon fields only) - keeps
// the ~500KB marketingPages.js content module out of the main bundle.
// MarketingPage loads the full page content itself, on demand, via a
// dynamic import.
const MARKETING_PAGE_PATHS = MARKETING_PAGE_META.map((page) => page.path);

// These must be real components (not inline arrow functions passed to
// Route's render/component props) so useHasTokenAfterMount's hook calls
// stay valid, and so they re-run on every navigation, not just once.
function RootRoute() {
    const hasToken = useHasTokenAfterMount();

    if (window.location.hostname === 'launch.luckynote.io') {
        return <LaunchPage />;
    }

    return hasToken ? <Home /> : <LandingHome />;
}

function RegisterRoute() {
    const hasToken = useHasTokenAfterMount();
    return hasToken ? <Redirect to="/home" /> : <Register />;
}

function LoginRoute() {
    const hasToken = useHasTokenAfterMount();
    const pendingLogoutRedirect = getPostLogoutRedirect();
    if (pendingLogoutRedirect === '/account-deleted') {
        return <Redirect to={pendingLogoutRedirect} />;
    }
    return hasToken ? <Redirect to="/home" /> : <Login />;
}

function Routes() {
    // Prefetch the main app chunk during idle time so the first navigation
    // into the authenticated app doesn't wait on a network fetch. Only for
    // visitors with a token — anonymous landing visitors (and Lighthouse)
    // shouldn't pay for downloading and evaluating the whole app bundle.
    useEffect(() => {
        const prefetch = () => {
            if (localStorage.getItem('token')) {
                import('./screens/Home');
            }
        };
        if ('requestIdleCallback' in window) {
            window.requestIdleCallback(prefetch, { timeout: 5000 });
        } else {
            setTimeout(prefetch, 3000);
        }
    }, []);

    return (
        <BrowserRouter>
            <ProductTranslationRuntime />
            <ScrollToTop />
            <RouteSeo />
            <AppContainer>
                <React.Suspense fallback={<AppChunkFallback />}>
                <Switch>
                    <Route exact path="/" component={RootRoute} />
                    <Route path="/register" render={() => <RegisterRoute />} />
                    <Route path="/login" render={() => <LoginRoute />} />
                    <Route path="/home" render={() => <Redirect to="/"></Redirect>} />
                    <Route path="/f/daily-review" component={LegacyDigestRedirect} />
                    <Route
                        path={["/f/:folderId", "/published/:token"]}
                        render={({ location }) => {
                            if (localStorage.getItem('token')) {
                                return <Home />;
                            } else {
                                return <Redirect to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`} />;
                            }
                        }}
                    />
                    <Route
                        path="/message/:id"
                        render={() => {
                            if (localStorage.getItem('token')) {
                                return <MessageDeepLink />;
                            } else {
                                return <Redirect to="/login" />;
                            }
                        }}
                    />
                    <Route path="/s/:token" component={PublishedLinkRouter} />
                    <Route exact path="/reminders" render={() => <Redirect to="/reminders/previous" />} />
                    <Route
                        path="/reminders/upcoming"
                        render={() => {
                            if (localStorage.getItem('token')) {
                                return <Home />;
                            } else {
                                return <Redirect to="/login" />;
                            }
                        }}
                    />
                    <Route
                        path="/reminders/previous"
                        render={() => {
                            if (localStorage.getItem('token')) {
                                return <Home />;
                            } else {
                                return <Redirect to="/login" />;
                            }
                        }}
                    />
                    {/* <Route path="/home/:id" component={Home} /> */}
                    <Route exact path="/compare" component={ComparePage} />
                    <Route exact path="/for" component={ForPage} />
                    <Route exact path="/use-cases" component={UseCasesPage} />
                    <Route exact path="/message-yourself" component={MessageYourselfPage} />
                    <Route exact path="/guides" component={GuidesPage} />
                    <Route exact path="/download/cli" component={CliDownloadPage} />
                    <Route exact path="/extension" component={ExtensionOnboarding} />
                    {MARKETING_PAGE_PATHS.map((path) => (
                        <Route
                            key={path}
                            exact
                            path={path}
                            render={() => <MarketingPage path={path} />}
                        />
                    ))}
                    <Redirect exact from="/use-cases/ai-notes" to="/ai-notes" />
                    <Redirect exact from="/use-cases/chatgpt-for-notes" to="/chatgpt-notes" />
                    <Redirect exact from="/compare/pocket-alternative" to="/compare/pocket-alternatives" />
                    <Redirect exact from="/best-pocket-alternatives" to="/compare/pocket-alternatives" />
                    <Redirect exact from="/download/desktop" to="/download-app" />
                    <Route path="/mobile" component={Mobile} />
                    <Route path="/solucky" component={Admin} />
                    <Route path="/verified" component={Verified} />
                    <Route path="/check-account" component={VerifyAccountPage} />
                    <Route path="/oauth/consent" component={OAuthConsentPage} />
                    <Route path="/extension-auth" component={ExtensionAuthPage} />
                    <Route path="/raycast-auth" component={RaycastAuthPage} />
                    <Route path="/forgot-password" component={ForgotPasswordPage} />
                    <Route path="/error" component={ErrorPage} />
                    <Route path="/x-auth-error" component={XOAuthErrorPage} />
                    <Route path="/privacy" component={Privacy} />
                    <Route path="/terms" component={Terms} />
                    <Route path="/contact" component={Contact} />
                    <Route path="/data-security" component={DataSecurity} />
                    <Route path="/press" component={Press} />
                    <Route exact path="/about/uros" component={AuthorPage} />
                    <Route path="/about" component={About} />
                    <Route path="/reset-password/:token" component={ResetPasswordPage} />
                    <Route
                        path="/checkout"
                        render={() => (
                            <StripeProvider>
                                <Checkout hideNavigation showNavigation />
                            </StripeProvider>
                        )}
                    />
                    <Route path="/purchases" component={Purchases} />
                    <Route path="/payment-success" component={PaymentSuccess} />
                    <Route path="/payment-cancelled" component={PaymentCancel} />
                    <Route path="/trial-feedback" component={TrialFeedbackPage} />
                    <Route path="/account-deleted" component={AccountDeleted} />
                    <Route path="/blog" exact component={BlogList} />
                    <Route path="/blog/category/:categorySlug" exact component={BlogList} />
                    <Route path="/blog/:slug" exact component={BlogPost} />
                    <Route path="/changelog" exact component={ChangelogPage} />
                    <Route path="/launch" exact component={LaunchPage} />
                    <Route path="/claude" exact component={ClaudeMcpPage} />
                    <Route path="/chatgpt" exact component={ChatGptMcpPage} />
                    <Route path="/tools/media-manager" component={MediaManagerPage} />
                    <Route path="/tools/integrations" component={IntegrationsPage} />
                    <Route path="/local/impersonation" component={LocalImpersonationPage} />
                    <Route exact path="/reviews" component={ReviewsPage} />
                    <Route exact path="/reviews/:id" component={ReviewDetailPage} />
                    <Route exact path="/wrapped" component={WrappedPage} />
                    <Route exact path="/shared" render={({ location }) => (
                        localStorage.getItem('token')
                            ? <Home />
                            : <Redirect to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`} />
                    )} />
                    <Route exact path="/shared-message/:messageId" component={SharedMessagePage} />
                    <Route exact path="/shared/:folderId" component={SharedFolderPage} />
                    <Route component={NotFoundPage} />
                </Switch>
                </React.Suspense>
            </AppContainer>
        </BrowserRouter>
    );
}

export default Routes;
